import type { TCreateAddressInputDTO } from '@doutorsim/shared/addresses'
import { normalizeIsoSubdivision } from '@doutorsim/shared/helpers/normalize-iso-subdivision'

type RowStateMap = Record<string, { checked: boolean; value: string }>

type SubdivisionId = TCreateAddressInputDTO['subdivisionId']

const ADDRESS_ROW_KEYS = [
  'address.postcode',
  'address.thoroughfare',
  'address.premiseNumber',
  'address.subPremise',
  'address.premise',
  'address.locality',
  'address.subdivisionId',
] as const

const toNullable = (value: string) => {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

/**
 * Builds the `TCreateAddressInputDTO` payload for the confirmation flow from
 * row state. Returns null when every address field is empty — the staff is
 * effectively saying "no address on file." When non-null, the backend resolves
 * whether to create or update based on the person's existing address.
 */
export const buildCreateAddress = (rows: RowStateMap): TCreateAddressInputDTO | null => {
  const get = (key: string) => rows[key]?.value ?? ''
  const allEmpty = ADDRESS_ROW_KEYS.every((key) => toNullable(get(key)) === null)
  if (allEmpty) return null

  return {
    countryId: 'BR',
    subdivisionId: normalizeIsoSubdivision(get('address.subdivisionId')) as SubdivisionId,
    postcode: toNullable(get('address.postcode')),
    thoroughfare: toNullable(get('address.thoroughfare')),
    premiseNumber: toNullable(get('address.premiseNumber')),
    subPremise: toNullable(get('address.subPremise')),
    premise: toNullable(get('address.premise')),
    locality: toNullable(get('address.locality')),
    poBox: null,
    coordinates: null,
  }
}
